import { useState, type ChangeEvent, type FormEvent } from 'react';
import type { ApiClient, ImportResult } from '@gc/api-client';
import { useAction } from '@gc/ui-state';

export type ImportApi = Pick<ApiClient, 'importCsv'>;

export function ImportCsv({ api, onImported, onCancel }: {
  api: ImportApi;
  onImported: () => void;
  onCancel: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const { busy, failure, run } = useAction();

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void f.text().then(setCsv);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    void run(async () => {
      const r = await api.importCsv(csv);
      setResult(r);
      if (r.failures.length === 0) onImported();
    });
  };

  return (
    <form className="form" onSubmit={submit} data-testid="import-form">
      <h3>Import gadgets from CSV</h3>
      <p>The whole file is checked first. If any row is wrong, nothing is imported and every problem is listed.</p>
      {failure && <p role="alert" data-testid="import-failure">{failure}</p>}
      <label>CSV file<input type="file" accept=".csv,text/csv" onChange={pick} /></label>
      <label>Or paste it<textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="import-csv" /></label>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || csv.trim() === ''} data-testid="import-submit">
          {busy ? 'Importing…' : 'Import'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>Close</button>
      </div>

      {result && result.failures.length === 0 && (
        <p data-testid="import-done">Imported {result.imported} gadget{result.imported === 1 ? '' : 's'}.</p>
      )}
      {result && result.failures.length > 0 && (
        <div className="scroll-x">
          <table data-testid="import-problems">
            <caption>Nothing was imported. Fix these and try again.</caption>
            <thead><tr><th scope="col">Line</th><th scope="col">Field</th><th scope="col">Problem</th></tr></thead>
            <tbody>
              {result.failures.map((f, i) => (
                <tr key={`${f.line}-${f.field}-${i}`}><td>{f.line}</td><td>{f.field}</td><td>{f.message}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </form>
  );
}
